// components
import {
  DesktopDatePicker,
  FieldSelectedSections
} from '@mui/x-date-pickers-pro';
import { IconButton } from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

// hooks
import { forwardRef, Ref, useRef, useState } from 'react';

// styles
import {
  StyledCustomDatePickerTextField,
  StyledMainDatePickerBox
} from './DatePicker.styles';

// types
import { DatePickerProps } from './DatePicker.types';
import { toDate } from './DatePicker.utils';
import { PickerValue } from '@mui/x-date-pickers/internals';

// utils
import { startOfToday } from 'date-fns';
import { getMaxDate, getMinDate } from 'utils/date';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const bottomDate = getMinDate();
const topDate = getMaxDate();

/**
 * Slot components must keep a stable identity: an inline arrow gives React a
 * new component type on every render, which unmounts and remounts the input
 * (losing focus and blinking the field). Everything dynamic goes through
 * slotProps instead.
 */
const HiddenTextField = () => null;

const DatePicker = forwardRef(
  (props: DatePickerProps, ref: Ref<HTMLDivElement> | undefined) => {
    const {
      date: _date,
      showCalendarIconOnly,
      label,
      minDate: _minDate,
      maxDate: _maxDate,
      open: _open,
      readOnly,
      monthYearView,
      required,
      onChange,
      disabled,
      error,
      clearable = true,
      variant = 'outlined',
      size = 'small',
      autoFocus,
      sx,
      onBlur,
      shouldDisableDate,
      commitOnChange
    } = props;

    const date = _date || startOfToday();

    const minDate = _minDate || bottomDate;
    const maxDate = _maxDate || topDate;
    const [newDate, setNewDate] = useState<PickerValue>(date);
    const [selectedSection, setSelectedSection] =
      useState<FieldSelectedSections>(null);
    const isValidNewDate = dayjs(newDate).isValid();
    const [textFieldFocus, setTextFieldFocus] = useState(autoFocus);
    const [open, setOpen] = useState(_open || false);

    const inputRef = useRef<HTMLInputElement>(null);
    const iconButtonRef = useRef<HTMLButtonElement>(null);

    // Sync a changed date prop during render, not in an effect: an effect
    // paints one frame with the previous value first, which shows up as a
    // flash when something else moves this field (e.g. a coupled date being
    // dragged along).
    const [previousDate, setPreviousDate] = useState(_date);
    if (_date !== previousDate) {
      setPreviousDate(_date);
      if (_date) setNewDate(_date);
    }

    return (
      <StyledMainDatePickerBox>
        <DesktopDatePicker
          className="date-picker"
          minDate={minDate}
          maxDate={maxDate}
          shouldDisableDate={shouldDisableDate}
          onChange={(value) => {
            if (!dayjs(value).isValid()) return;
            setTextFieldFocus(true);

            const valueOnChange = toDate(value);
            if (valueOnChange) setNewDate?.(value);

            showCalendarIconOnly && onChange?.(dayjs(value).toDate());

            // Typing runs through half-finished dates ("08-08-0002" on the
            // way to 2026), so only a value inside the allowed range is
            // reported early; the rest waits for blur as before. The value is
            // marked as typed: a date section that is still being filled in
            // reads as a complete date ("01" on the way to "14"), which the
            // caller must not mistake for a deliberate choice.
            if (commitOnChange && !showCalendarIconOnly) {
              const typed = dayjs(value);
              const withinBounds =
                !typed.isBefore(dayjs(minDate), 'day') &&
                !typed.isAfter(dayjs(maxDate), 'day');

              if (withinBounds) onChange?.(typed.toDate(), { typing: true });
            }
          }}
          // a calendar pick is a deliberate final choice: commit it to the
          // parent immediately, so dependent fields (e.g. a coupled end date)
          // move along at once instead of snapping later on blur. Typed input
          // keeps committing on blur/Enter only, unless commitOnChange is set —
          // which already reported this value, so no second call here.
          onAccept={(value) => {
            if (commitOnChange || !dayjs(value).isValid()) return;
            onChange?.(dayjs(value).toDate());
          }}
          selectedSections={selectedSection}
          onSelectedSectionsChange={setSelectedSection}
          slots={{
            textField: showCalendarIconOnly
              ? HiddenTextField
              : StyledCustomDatePickerTextField
          }}
          slotProps={{
            field: { clearable },
            textField: {
              inputRef,
              inputProps: { tabIndex: 0 },
              InputProps: { tabIndex: -1 },
              fullWidth: true,
              required,
              error: !!error,
              helperText: error?.message ?? null,
              variant,
              size,
              sx,
              autoFocus: textFieldFocus,
              onBlur: () => {
                if (isValidNewDate) {
                  onChange?.(dayjs(newDate).toDate());
                  setTextFieldFocus(false);
                  setSelectedSection(0);
                }
                onBlur?.();
              },
              onKeyDown: (e) => {
                if (e.defaultPrevented || e.code !== 'Enter' || !isValidNewDate)
                  return;

                onChange?.(dayjs(newDate).toDate());
                setTextFieldFocus(false);
              },
              onKeyDownCapture: (e) => {
                if (
                  (e.code === 'Delete' || e.code === 'Backspace') &&
                  selectedSection === 'all'
                ) {
                  setNewDate(null);
                  setTextFieldFocus(true);
                }
              },
              onClickCapture: (e) => {
                const target = e.target as HTMLElement;
                if (target.closest('button')) return;
                setTextFieldFocus(true);
              }
            },
            clearButton: {
              onClickCapture: () => {
                setNewDate(null);
                setTextFieldFocus(true);
              },
              tabIndex: -1
            },
            openPickerButton: {
              tabIndex: -1
            },
            ...(showCalendarIconOnly && {
              popper: {
                anchorEl: iconButtonRef.current
              }
            })
          }}
          enableAccessibleFieldDOMStructure={false}
          displayWeekNumber
          value={newDate}
          label={label}
          open={open}
          onOpen={() => setOpen(true)}
          onClose={() => setOpen(false)}
          readOnly={readOnly}
          disabled={disabled}
          ref={ref}
          views={monthYearView ? ['year', 'month'] : ['year', 'month', 'day']}
        />
        {showCalendarIconOnly && (
          <IconButton ref={iconButtonRef} onClick={() => setOpen(true)}>
            <CalendarMonthIcon />
          </IconButton>
        )}
      </StyledMainDatePickerBox>
    );
  }
);

export default DatePicker;
